import { StatCard, StatCardGrid } from '../../../components/common';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate } from '../../../utils/dateUtils';
import styles from '../Reports.module.css';

const STATUS_LABEL = {
  pending:   'Pending',
  partial:   'Partial',
  completed: 'Paid',
  cancelled: 'Cancelled',
};
const STATUS_COLOR = {
  pending:   'var(--color-error, #dc2626)',
  partial:   'var(--color-warning, #d97706)',
  completed: 'var(--color-success, #059669)',
  cancelled: 'var(--color-text-muted, #6b7280)',
};

function StatusBadge({ status }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: '0.72rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      background: STATUS_COLOR[status] || 'var(--color-text-muted)',
      color: '#fff',
      whiteSpace: 'nowrap',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function OfferingsReport({ data, filterMemberName }) {
  if (!data) return <div className="empty-state">Select filters and click Run to view offerings.</div>;
  const { rows = [], summary = {} } = data;

  const displayRows = filterMemberName
    ? rows.filter((r) => (r.memberName || '').toLowerCase().includes(filterMemberName.toLowerCase()))
    : rows;

  const displaySummary = filterMemberName ? {
    totalOffered:   displayRows.reduce((s, r) => s + (Number(r.offeredAmount)  || 0), 0),
    totalPaid:      displayRows.reduce((s, r) => s + (Number(r.paidAmount)     || 0), 0),
    totalPending:   displayRows.reduce((s, r) => s + (Number(r.pendingAmount)  || 0), 0),
    countPending:   displayRows.filter((r) => r.status === 'pending').length,
    countPartial:   displayRows.filter((r) => r.status === 'partial').length,
    countCompleted: displayRows.filter((r) => r.status === 'completed').length,
    countCancelled: displayRows.filter((r) => r.status === 'cancelled').length,
  } : summary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StatCardGrid cols={3}>
        <StatCard label="Total Offered" value={fmtCurrency(displaySummary.totalOffered)} />
        <StatCard label="Total Paid"    value={fmtCurrency(displaySummary.totalPaid)}    valueColor="green" />
        <StatCard label="Total Pending" value={fmtCurrency(displaySummary.totalPending)} valueColor="red" />
      </StatCardGrid>

      <StatCardGrid cols={4}>
        <StatCard label="Pending"   value={displaySummary.countPending}   valueColor="red" />
        <StatCard label="Partial"   value={displaySummary.countPartial}   valueColor="red" />
        <StatCard label="Paid"      value={displaySummary.countCompleted} valueColor="green" />
        <StatCard label="Cancelled" value={displaySummary.countCancelled || 0} />
      </StatCardGrid>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Offer No</th>
                <th>Offering Date</th>
                <th>Member</th>
                <th>Category</th>
                <th className={styles.amtRight}>Offered (Rs)</th>
                <th className={styles.amtRight}>Paid (Rs)</th>
                <th className={styles.amtRight}>Outstanding</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 && (
                <tr><td colSpan={9} className="empty-state">No offerings found for the selected filters.</td></tr>
              )}
              {displayRows.map((r, i) => (
                <tr key={r.id} style={r.status === 'cancelled' ? { opacity: 0.55 } : {}}>
                  <td>{i + 1}</td>
                  <td><code>{r.offerNo || '—'}</code></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.offeringDate)}</td>
                  <td>{r.memberName}</td>
                  <td>{r.category}</td>
                  <td className={styles.amtRight}>{fmtCurrency(r.offeredAmount)}</td>
                  <td className={styles.amtRight}>{fmtCurrency(r.paidAmount)}</td>
                  <td className={styles.amtRight}>{fmtCurrency(r.pendingAmount)}</td>
                  <td><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
            {displayRows.filter((r) => r.status !== 'cancelled').length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={5}><strong>Total (excl. cancelled)</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(displaySummary.totalOffered)}</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(displaySummary.totalPaid)}</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(displaySummary.totalPending)}</strong></td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
